/**
 * useOffline: the offline state store (the state.ts reducer) that the badge,
 * the presenter and the shell read. serviceWorker.ts dispatches into it.
 */
import { create } from 'zustand';
import { initialOfflineState, reduceOffline, type OfflineEvent, type OfflineState } from './state';

export interface OfflineActions {
  dispatch: (event: OfflineEvent) => void;
  /** Activates the waiting service worker; the page reloads once it takes control. */
  applyUpdate: () => void;
}

let updateHandler: (() => void) | null = null;

/** serviceWorker.ts installs the function that activates a waiting worker. */
export function setUpdateHandler(handler: () => void): void {
  updateHandler = handler;
}

const inBrowser = typeof window !== 'undefined';

export const useOffline = create<OfflineState & OfflineActions>()((set) => ({
  ...initialOfflineState(
    inBrowser ? navigator.onLine : true,
    inBrowser && 'serviceWorker' in navigator ? navigator.serviceWorker.controller !== null : false,
  ),
  dispatch: (event) => set((s) => reduceOffline(s, event)),
  applyUpdate: () => {
    if (!updateHandler)
      throw new Error('applyUpdate: no service worker is registered, so no update can be waiting');
    updateHandler();
  },
}));
